import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {
    BarElement,
    BarController,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from 'chart.js'
import moment from 'moment'
import {Chart} from 'react-chartjs-2'
import {ToastContainer} from 'react-toastify'
import {KTIcon} from '../../../../../_websoft/helpers'
import {otpremaKamionaIzvjestaj} from '../../../../api/izvjestaj'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../../../components/Toast/Toast'
import TitleComponent from '../../../../components/TitleComponent/TitleComponent'

type OtpremaTruck = {
  RedniBroj: number
  Vozac?: string | null
  Registracija?: string | null
  IzdatBroj?: string | number | null
  Kreirano: string | null
  PocetakUtovara: string | null
  KrajUtovara: string | null
    vrijeme_cekanja_za_utovar_seconds?: number | string
    vrijeme_cekanja_za_utovar: string | null
    trajanje_utovara_seconds?: number | string
    trajanje_utovara: string | null
}

type OtpremaKupac = {
    Kupac: string | null
    total_trucks: number
    total_waited_time_seconds?: number | string
    total_waited_time: string
    total_loading_time_seconds?: number | string
    total_loading_time: string
    trucks?: OtpremaTruck[]
}

type OtpremaVozac = {
    Vozac: string | null
    total_trucks: number
    total_waited_time_seconds?: number | string
    total_waited_time?: string
    total_loading_time_seconds?: number | string
    total_loading_time?: string
}

type OtpremaReport = {
    period: {
        datumOd: string
        datumDo: string
    }
    summary: {
        total_customers: number
        total_trucks: number
        total_waited_time: string
        total_loading_time: string
    }
    kupci: OtpremaKupac[]
    vozaci: OtpremaVozac[]
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarController,
    BarElement,
    Tooltip,
    Legend
)

const today = moment().format('YYYY-MM-DD')

const formatDateTime = (value: string | null) => {
    if (!value) {
        return '-'
    }

    return moment(value).isValid() ? moment(value).format('DD.MM.YYYY HH:mm') : value
}

const normalizeSeconds = (seconds?: number | string) => {
  const value = Number(seconds)
  return Number.isFinite(value) && value >= 0 ? value : 0
}

const secondsToHours = (seconds?: number | string) => normalizeSeconds(seconds) / 3600

const hoursToTime = (hours: number) => {
  const totalSeconds = Math.round((hours || 0) * 3600)
  const formattedHours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return `${String(formattedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const OtpremaPanelaIzvjestaj: React.FC = () => {
    const [datumOd, setDatumOd] = useState(today)
    const [datumDo, setDatumDo] = useState(today)
    const [listaKamiona, setListaKamiona] = useState(false)
    const [loading, setLoading] = useState(false)
    const [report, setReport] = useState<OtpremaReport | null>(null)
    const [activeTab, setActiveTab] = useState<'charts' | 'data'>('charts')
    const [activeChartTab, setActiveChartTab] = useState<'trucks' | 'customersTime' | 'driversTime'>('trucks')

    const sortedKupci = useMemo(() => {
        return [...(report?.kupci ?? [])].sort((a, b) => (b.total_trucks ?? 0) - (a.total_trucks ?? 0))
    }, [report])

    const chartKupci = useMemo(() => {
        return [...sortedKupci].reverse()
    }, [sortedKupci])

    const trucksChartData = useMemo(() => {
        return {
            labels: chartKupci.map((kupac) => kupac.Kupac?.trim() || 'Nepoznat kupac'),
            datasets: [
                {
                    label: 'Broj kamiona',
                    data: chartKupci.map((kupac) => kupac.total_trucks || 0),
                    backgroundColor: '#1b84ff',
                    borderColor: '#1b84ff',
                    borderRadius: 8,
                    barThickness: 18,
                },
            ],
        }
    }, [chartKupci])

    const timeChartData = useMemo(() => {
        return {
            labels: chartKupci.map((kupac) => kupac.Kupac?.trim() || 'Nepoznat kupac'),
            datasets: [
                {
                    label: 'Cekanje (h)',
                    data: chartKupci.map((kupac) => secondsToHours(kupac.total_waited_time_seconds)),
                    backgroundColor: '#f6c000',
                    borderColor: '#f6c000',
                    borderRadius: 8,
                    barThickness: 12,
                },
                {
                    label: 'Utovar (h)',
                    data: chartKupci.map((kupac) => secondsToHours(kupac.total_loading_time_seconds)),
                    backgroundColor: '#50cd89',
                    borderColor: '#50cd89',
                    borderRadius: 8,
                    barThickness: 12,
                },
            ],
        }
    }, [chartKupci])

    const driverStats = useMemo(() => {
        return [...(report?.vozaci ?? [])]
            .map((driver) => ({
                vozac: driver.Vozac?.trim() || 'Nepoznat vozac',
                waitedSeconds: normalizeSeconds(driver.total_waited_time_seconds),
                loadingSeconds: normalizeSeconds(driver.total_loading_time_seconds),
                trucks: Number(driver.total_trucks) || 0,
            }))
            .sort((a, b) => a.trucks - b.trucks)
    }, [report])

    const driverTimeChartData = useMemo(() => {
        return {
            labels: driverStats.map((driver) => driver.vozac),
            datasets: [
                {
                    label: 'Broj kamiona',
                    data: driverStats.map((driver) => driver.trucks),
                    backgroundColor: '#1b84ff',
                    borderColor: '#1b84ff',
                    borderRadius: 8,
                    barThickness: 12,
                },
                {
                    label: 'Cekanje (h)',
                    data: driverStats.map((driver) => secondsToHours(driver.waitedSeconds)),
                    backgroundColor: '#f6c000',
                    borderColor: '#f6c000',
                    borderRadius: 8,
                    barThickness: 12,
                },
                {
                    label: 'Utovar (h)',
                    data: driverStats.map((driver) => secondsToHours(driver.loadingSeconds)),
                    backgroundColor: '#50cd89',
                    borderColor: '#50cd89',
                    borderRadius: 8,
                    barThickness: 12,
                },
            ],
        }
    }, [driverStats])

    const baseChartOptions: any = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const value = Number(context.raw || 0).toLocaleString()
                        return `${context.dataset.label}: ${value}`
                    },
                },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
                grid: {
                    color: '#eef3f7',
                },
            },
            y: {
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkip: false,
                },
            },
        },
    }

    const trucksChartOptions: any = {
        ...baseChartOptions,
        plugins: {
            ...baseChartOptions.plugins,
            legend: {
                display: false,
            },
        },
        scales: {
            ...baseChartOptions.scales,
            x: {
                ...baseChartOptions.scales.x,
                title: {
                    display: true,
                    text: 'Broj kamiona',
                },
            },
        },
    }

    const timeChartOptions: any = {
        ...baseChartOptions,
        plugins: {
            ...baseChartOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${hoursToTime(Number(context.raw || 0))}`,
        },
      },
    },
        scales: {
            ...baseChartOptions.scales,
            x: {
                ...baseChartOptions.scales.x,
                ticks: {
                    precision: 1,
                },
                title: {
                    display: true,
                    text: 'Sati',
                },
            },
        },
    }

    const driverTimeChartOptions: any = {
        ...timeChartOptions,
        plugins: {
            ...timeChartOptions.plugins,
            tooltip: {
                callbacks: {
          label: (context: any) => {
            const value = Number(context.raw || 0).toLocaleString()
            return context.dataset.label === 'Broj kamiona'
              ? `${context.dataset.label}: ${value}`
              : `${context.dataset.label}: ${hoursToTime(Number(context.raw || 0))}`
          },
        },
      },
        },
        scales: {
            ...timeChartOptions.scales,
            x: {
                ...timeChartOptions.scales.x,
                title: {
                    display: true,
                    text: 'Sati / broj kamiona',
                },
            },
        },
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!datumOd || !datumDo) {
            notifyToast(false, 'Datum od i datum do su obavezni.')
            return
        }

        if (moment(datumOd).isAfter(moment(datumDo))) {
            notifyToast(false, 'Datum od ne moze biti veci od datuma do.')
            return
        }

        setLoading(true)

        try {
            const response = await otpremaKamionaIzvjestaj({
                datumOd,
                datumDo,
                listaKamiona,
            })

            const data = response.data?.data

            if (!data) {
                setReport(null)
                notifyToast(false, response.data?.message || 'Nema podataka za odabrani period.')
                return
            }

            setReport(data)
            setActiveTab('charts')
            setActiveChartTab('trucks')
        } catch (error: any) {
            console.error('Otprema kamiona izvjestaj error:', error)
            notifyToast(false, error.response?.data?.message || 'Greska prilikom ucitavanja izvjestaja.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='card mb-5 mb-xl-10'>
            <ToastContainer/>

            <div className='card-header border-0'>
                <div className='card-title m-0'>
                    <TitleComponent name='Otprema kamiona'/>
                </div>
            </div>

            <div className='card-body border-top p-9'>
                <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-8'>
                    <div className='d-flex flex-column'>
                        <div className='fw-bolder text-gray-900 mb-1'>Napomena o podacima</div>
                        <div className='text-gray-700'>
                            Potpuni podaci o vozacima, kupcima i informacijama vezanim za kamion dostupni su od
                            17.06.2026.
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className='mb-10'>
                    <div className='row g-6 align-items-end'>
                        <div className='col-lg-3 col-md-6'>
                            <label className='form-label fw-bold'>Datum od</label>
                            <input
                                type='date'
                                value={datumOd}
                                onChange={(event) => setDatumOd(event.target.value)}
                                className='form-control form-control-lg form-control-solid'
                            />
                        </div>

                        <div className='col-lg-3 col-md-6'>
                            <label className='form-label fw-bold'>Datum do</label>
                            <input
                                type='date'
                                value={datumDo}
                                onChange={(event) => setDatumDo(event.target.value)}
                                className='form-control form-control-lg form-control-solid'
                            />
                        </div>

                        <div className='col-lg-3 col-md-6'>
                            <label className='form-label fw-bold'>Prikazi kamione za svakog kupca</label>
                            <select
                                value={listaKamiona ? 'true' : 'false'}
                                onChange={(event) => setListaKamiona(event.target.value === 'true')}
                                className='form-select form-select-lg form-select-solid'
                            >
                                <option value='false'>Ne</option>
                                <option value='true'>Da</option>
                            </select>
                        </div>

                        <div className='col-lg-3 col-md-6'>
                            <button type='submit' className='btn btn-primary w-100' disabled={loading}>
                                {loading ? (
                                    <span className='indicator-progress' style={{display: 'block'}}>
                    Ucitavanje...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'/>
                  </span>
                                ) : (
                                    <>
                                        <KTIcon iconName='magnifier' className='fs-2'/>
                                        Prikazi izvjestaj
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>

                {loading && <LoadingComponent/>}

                {!loading && report && (
                    <>
                        <div className='d-flex flex-wrap justify-content-between align-items-center gap-4 mb-7'>
                            <div>
                                <div className='text-muted fw-semibold fs-7 text-uppercase'>Period izvjestaja</div>
                                <div className='fs-3 fw-bolder text-gray-900'>
                                    {moment(report.period.datumOd).format('DD.MM.YYYY')} - {moment(report.period.datumDo).format('DD.MM.YYYY')}
                                </div>
                            </div>
                            <span
                                className={clsx('badge fs-7 py-3 px-4', listaKamiona ? 'badge-light-success' : 'badge-light-primary')}>
                {listaKamiona ? 'Prikazani kamioni po kupcu' : 'Zbirni prikaz po kupcu'}
              </span>
                        </div>

                        <div className='row g-6 mb-9'>
                            <div className='col-xl-3 col-md-6'>
                                <div className='border border-gray-300 rounded p-6 h-100 bg-light-primary'>
                                    <div className='text-muted fw-semibold mb-2'>Ukupno kupaca</div>
                                    <div className='fs-1 fw-bolder text-gray-900'>{report.summary.total_customers}</div>
                                </div>
                            </div>
                            <div className='col-xl-3 col-md-6'>
                                <div className='border border-gray-300 rounded p-6 h-100 bg-light-info'>
                                    <div className='text-muted fw-semibold mb-2'>Ukupno kamiona</div>
                                    <div className='fs-1 fw-bolder text-gray-900'>{report.summary.total_trucks}</div>
                                </div>
                            </div>
                            <div className='col-xl-3 col-md-6'>
                                <div className='border border-gray-300 rounded p-6 h-100 bg-light-warning'>
                                    <div className='text-muted fw-semibold mb-2'>Ukupno cekanje</div>
                                    <div
                                        className='fs-2 fw-bolder text-gray-900'>{report.summary.total_waited_time}</div>
                                </div>
                            </div>
                            <div className='col-xl-3 col-md-6'>
                                <div className='border border-gray-300 rounded p-6 h-100 bg-light-success'>
                                    <div className='text-muted fw-semibold mb-2'>Ukupno trajanje utovara</div>
                                    <div
                                        className='fs-2 fw-bolder text-gray-900'>{report.summary.total_loading_time}</div>
                                </div>
                            </div>
                        </div>

                        <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-8 fs-6'>
                            <li className='nav-item'>
                                <button
                                    type='button'
                                    className={clsx('nav-link fw-bold', activeTab === 'charts' && 'active')}
                                    onClick={() => setActiveTab('charts')}
                                >
                                    Grafikoni
                                </button>
                            </li>
                            <li className='nav-item'>
                                <button
                                    type='button'
                                    className={clsx('nav-link fw-bold', activeTab === 'data' && 'active')}
                                    onClick={() => setActiveTab('data')}
                                >
                                    Podaci
                                </button>
                            </li>
                        </ul>

                        {activeTab === 'charts' && sortedKupci.length > 0 && (
                            <div className='card border border-gray-300 mb-9'>
                                <div className='card-header border-0 pt-6'>
                                    <div>
                                        <h3 className='card-title fw-bolder text-gray-900 mb-1'>Kupci, kamioni, cekanje
                                            i utovar</h3>
                                        <div className='text-muted fw-semibold fs-7'>
                                            Vrijeme je prikazano u satima radi lakseg poredjenja.
                                        </div>
                                    </div>
                                </div>
                                <div className='card-body pt-0'>
                                    <ul className='nav nav-pills mb-8'>
                                        <li className='nav-item me-2'>
                                            <button
                                                type='button'
                                                className={clsx('nav-link fw-bold', activeChartTab === 'trucks' && 'active')}
                                                onClick={() => setActiveChartTab('trucks')}
                                            >
                                                Kamioni po kupcu
                                            </button>
                                        </li>
                                        <li className='nav-item me-2'>
                                            <button
                                                type='button'
                                                className={clsx('nav-link fw-bold', activeChartTab === 'customersTime' && 'active')}
                                                onClick={() => setActiveChartTab('customersTime')}
                                            >
                                                Cekanje/utovar po kupcu
                                            </button>
                                        </li>
                                        <li className='nav-item'>
                                            <button
                                                type='button'
                                                className={clsx('nav-link fw-bold', activeChartTab === 'driversTime' && 'active')}
                                                onClick={() => setActiveChartTab('driversTime')}
                                            >
                                                Cekanje/utovar po vozacu
                                            </button>
                                        </li>
                                    </ul>

                                    {activeChartTab === 'trucks' && (
                                        <div>
                                            <div className='fw-bold text-gray-800 mb-3'>Broj kamiona po kupcu</div>
                                            <div style={{height: Math.max(320, chartKupci.length * 34)}}>
                                                <Chart type='bar' data={trucksChartData} options={trucksChartOptions}/>
                                            </div>
                                        </div>
                                    )}

                                    {activeChartTab === 'customersTime' && (
                                        <div>
                                            <div className='fw-bold text-gray-800 mb-3'>Cekanje naspram trajanja
                                                utovara
                                            </div>
                                            <div style={{height: Math.max(320, chartKupci.length * 42)}}>
                                                <Chart type='bar' data={timeChartData} options={timeChartOptions}/>
                                            </div>
                                        </div>
                                    )}

                                    {activeChartTab === 'driversTime' && driverStats.length > 0 && (
                                        <div>
                                            <div className='fw-bold text-gray-800 mb-3'>
                                                Broj kamiona, cekanje i trajanje utovara po vozacu
                                            </div>
                                            <div style={{height: Math.max(420, driverStats.length * 58)}}>
                                                <Chart type='bar' data={driverTimeChartData}
                                                       options={driverTimeChartOptions}/>
                                            </div>
                                        </div>
                                    )}

                                    {activeChartTab === 'driversTime' && driverStats.length === 0 && (
                                        <div
                                            className='notice d-flex bg-light-info rounded border-info border border-dashed p-6'>
                                            <div className='fw-bold text-gray-700'>
                                                Nema podataka po vozacu za odabrani period.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'charts' && sortedKupci.length === 0 && (
                            <div
                                className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                                <div className='fw-bold text-gray-700'>Nema kupaca za prikaz grafikona u odabranom
                                    periodu.
                                </div>
                            </div>
                        )}

                        {activeTab === 'data' && sortedKupci.length > 0 ? (
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gy-4'>
                                    <thead>
                                    <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                                        <th className='text-white ps-4 min-w-250px'>Kupac</th>
                                        <th className='text-white text-end min-w-125px'>Kamiona</th>
                                        <th className='text-white text-end min-w-160px'>Vrijeme cekanja</th>
                                        <th className='text-white text-end pe-4 min-w-180px'>Trajanje utovara</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {sortedKupci.map((kupac, index) => (
                                        <React.Fragment key={`${kupac.Kupac || 'bez-kupca'}-${index}`}>
                                            <tr>
                                                <td className='ps-4'>
                                                    <div
                                                        className='fw-bolder text-gray-900'>{kupac.Kupac?.trim() || 'Nepoznat kupac'}</div>
                                                    {listaKamiona && (
                                                        <div className='text-muted fs-7'>
                                                            {(kupac.trucks?.length ?? 0) > 0 ? `${kupac.trucks?.length} stavki utovara` : 'Nema stavki kamiona'}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className='text-end fw-bold'>{kupac.total_trucks}</td>
                                                <td className='text-end'>{kupac.total_waited_time}</td>
                                                <td className='text-end pe-4'>{kupac.total_loading_time}</td>
                                            </tr>

                                            {listaKamiona && (kupac.trucks?.length ?? 0) > 0 && (
                                                <tr>
                                                    <td colSpan={4} className='bg-light ps-8 pe-8 py-6'>
                                                        <div className='table-responsive'>
                                                            <table
                                                                className='table table-sm table-bordered align-middle mb-0 bg-white'>
                                                                <thead>
                                                                <tr className='fw-bold text-gray-700 bg-light'>
                                                                    <th>R. br.</th>
                                                                    <th>Vozac</th>
                                                                    <th>Registracija</th>
                                                                    <th>Izdat broj</th>
                                                                    <th>Pocetak utovara</th>
                                                                    <th>Kraj utovara</th>
                                                                    <th className='text-end'>Cekanje</th>
                                                                    <th className='text-end'>Utovar</th>
                                                                </tr>
                                                                </thead>
                                                                <tbody>
                                                                {kupac.trucks?.map((truck) => (
                                                                    <tr key={`${kupac.Kupac || 'bez-kupca'}-${truck.RedniBroj}`}>
                                                                        <td className='fw-bold'>{truck.RedniBroj}</td>
                                                                        <td>{truck.Vozac || '-'}</td>
                                                                        <td>{truck.Registracija || '-'}</td>
                                                                        <td>{formatDateTime(truck.Kreirano)}</td>
                                                                        <td>{formatDateTime(truck.PocetakUtovara)}</td>
                                                                        <td>{formatDateTime(truck.KrajUtovara)}</td>
                                                                        <td className='text-end'>{truck.vrijeme_cekanja_za_utovar || '-'}</td>
                                                                        <td className='text-end'>{truck.trajanje_utovara || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : activeTab === 'data' ? (
                            <div
                                className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                                <div className='fw-bold text-gray-700'>Nema kupaca za odabrani period.</div>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    )
}

export default OtpremaPanelaIzvjestaj
